import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Progress,
  Loader,
  Center,
  Badge,
  Paper,
  useMantineTheme,
} from "@mantine/core";
import {
  IconTrophy,
  IconArrowUpRight,
  IconArrowDownRight,
  IconBook2,
  IconFileText,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import "./styles.scss";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { AssessmentProps, AttemptProps } from "../../Lib/types/assessment";
import { Endpoints } from "../../Lib/endpoints";
import { LessonProps } from "../../Lib/types";

const getGradeColor = (score: number, theme: any) => {
  if (score >= 90) return theme.colors.green[6];
  if (score >= 75) return theme.colors.blue[6];
  if (score >= 60) return theme.colors.yellow[6];
  if (score >= 45) return theme.colors.orange[6];
  return theme.colors.red[6];
};

const CoursePerformance = () => {
  const { courseID } = useParams();
  const theme = useMantineTheme();
  const {
    data: performanceData,
    isLoading,
    error,
  } = usePerformRequest<
    NonPaginatedResponse<{
      attempts: AttemptProps[];
      assessments: AssessmentProps[];
      lessons: LessonProps[];
    }>
  >({
    method: Endpoints.GetStudentPerformanceInSingleCourse(courseID as string)
      .method,
    route: Endpoints.GetStudentPerformanceInSingleCourse(courseID as string)
      .route,
  });

  const attempts = performanceData?.data?.attempts || [];
  const assessments = performanceData?.data?.assessments || [];

  // Helper to get assessment title by ID
  const getAssessmentTitle = (assessmentID: string) => {
    const assessment = assessments.find((a) => a.id === assessmentID);
    return assessment ? assessment.title : "Assessment";
  };
  // Helper to get lesson title by assessmentID
  const getLessonTitle = (assessmentID: string) => {
    const assessment = assessments.find((a) => a.id === assessmentID);
    // If you have lesson info, return it here. Otherwise fallback:
    const lesson = performanceData?.data?.lessons?.find(
      (l) => l.id === assessment?.lessonID
    );
    return lesson ? lesson.title : "Lesson";
  };

  const average = useMemo(
    () =>
      attempts.length
        ? attempts.reduce((a, b) => a + b.score, 0) / attempts.length
        : 0,
    [attempts]
  );
  const best = useMemo(
    () => (attempts.length ? Math.max(...attempts.map((a) => a.score)) : 0),
    [attempts]
  );
  const worst = useMemo(
    () => (attempts.length ? Math.min(...attempts.map((a) => a.score)) : 0),
    [attempts]
  );

  return (
    <Container size="lg" className="course-performance-container">
      <Title order={2} ta="center" mb={32} className="performance-title">
        <IconTrophy
          size={32}
          style={{
            marginRight: 10,
            verticalAlign: "middle",
            color: theme.colors.violet[6],
          }}
        />
        Student Performance
      </Title>
      {isLoading ? (
        <Center h={300}>
          <Loader size="xl" color="violet" />
        </Center>
      ) : attempts.length === 0 ? (
        <Center h={220}>
          <Text size="lg" c="dimmed">
            No assessment attempts yet.
          </Text>
        </Center>
      ) : (
        <>
          {/* Performance Summary */}
          <Group justify="center" gap={24} mb={40}>
            <Paper
              radius={"md"}
              shadow="md"
              p="lg"
              className="summary-card"
              withBorder
            >
              <Stack align="center" gap={4}>
                <Text fz={15} c="dimmed">
                  Average
                </Text>
                <Text
                  fz={32}
                  fw={700}
                  style={{ color: getGradeColor(average, theme) }}
                >
                  {average.toFixed(1)}%
                </Text>
              </Stack>
            </Paper>
            <Paper
              radius={"md"}
              shadow="md"
              p="lg"
              className="summary-card"
              withBorder
            >
              <Stack align="center" gap={4}>
                <Text fz={15} c="dimmed">
                  Best
                </Text>
                <Group gap={6}>
                  <IconArrowUpRight size={20} color={theme.colors.green[6]} />
                  <Text
                    fz={24}
                    fw={700}
                    style={{ color: theme.colors.green[6] }}
                  >
                    {best}%
                  </Text>
                </Group>
              </Stack>
            </Paper>
            <Paper
              radius={"md"}
              shadow="md"
              p="lg"
              className="summary-card"
              withBorder
            >
              <Stack align="center" gap={4}>
                <Text fz={15} c="dimmed">
                  Lowest
                </Text>
                <Group gap={6}>
                  <IconArrowDownRight size={20} color={theme.colors.red[6]} />
                  <Text fz={24} fw={700} style={{ color: theme.colors.red[6] }}>
                    {worst}%
                  </Text>
                </Group>
              </Stack>
            </Paper>
          </Group>

          {/* Timeline/Grid of Attempts */}
          <Stack gap={24}>
            {attempts.map((attempt, idx) => (
              <motion.div
                key={attempt.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: idx * 0.07,
                  type: "spring",
                  stiffness: 80,
                }}
              >
                <Card
                  radius={"md"}
                  shadow="lg"
                  p="lg"
                  className="attempt-card"
                  withBorder
                  style={{
                    background:
                      (theme as any).colorScheme === "dark"
                        ? "linear-gradient(120deg, rgba(40,40,60,0.95) 60%, rgba(80,60,120,0.85) 100%)"
                        : "linear-gradient(120deg, rgba(255,255,255,0.95) 60%, rgba(180,160,255,0.13) 100%)",
                  }}
                >
                  <Group gap={18} align="center">
                    <Badge
                      size="lg"
                      radius="md"
                      color={getGradeColor(attempt.score, theme)}
                      variant="filled"
                      style={{ minWidth: 70, fontSize: 18 }}
                    >
                      {attempt.score}%
                    </Badge>
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Group gap={8}>
                        <IconFileText size={18} style={{ opacity: 0.7 }} />
                        <Text fw={600} fz={17}>
                          {/* Show assessment title */}
                          {getAssessmentTitle(attempt.assessmentID)}
                        </Text>
                      </Group>
                      <Group gap={8}>
                        <IconBook2 size={16} style={{ opacity: 0.6 }} />
                        <Text fz={15} c="dimmed">
                          {/* Show lesson title if available */}
                          {getLessonTitle(attempt.assessmentID)}
                        </Text>
                      </Group>
                      <Text fz={13} c="dimmed" mt={2}>
                        {dayjs(attempt.date).format("MMMM D, YYYY h:mm A")}
                      </Text>
                    </Stack>
                    <div style={{ minWidth: 120, flex: 0.5 }}>
                      <Progress
                        value={attempt.score}
                        color={getGradeColor(attempt.score, theme)}
                        radius="md"
                        size="lg"
                        striped
                        animated
                        style={{ boxShadow: theme.shadows.xs }}
                      />
                    </div>
                  </Group>
                </Card>
              </motion.div>
            ))}
          </Stack>
        </>
      )}
    </Container>
  );
};

export default CoursePerformance;
